import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { addToCart } from '../../store/cartSlice';
import { addToWishlist, removeFromWishlist } from '../../store/wishlistSlice';
import ShopNav from '../../components/shopNav/ShopNav';
import NavCategorias from '../../components/navbar/NavCategorias';
import Footer from '../../components/footer/footer';
import ProductFilter from '../../components/filters/ProductFilter';
import ServiceHighlights from '../../components/servicehighlights/ServiceHighlights';
import { showShopToast } from '../../utils/shopToast';
import { ProductImage } from '../../components/shop/ProductImageGallery';
import './SearchResults.css';

const API_URL = import.meta.env.VITE_API_URL;

const SearchResults = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 6;
  const [filters, setFilters] = useState({ search: '', sort: '' });

  const wishlistItems = useSelector(state => state.wishlist.items || []);
  const isInWishlist = id => wishlistItems.some(item => item.id === id);

  const formatCOP = value =>
    new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchQuery = params.get('q') || '';
    setQuery(searchQuery);
    setCurrentPage(1);
    if (searchQuery.trim()) {
      axios
        .get(`${API_URL}/buscar?q=${searchQuery}`)
        .then(res => setResults(res.data))
        .catch(err => console.error('❌ Error en búsqueda:', err))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [location.search]);

  const filteredResults = useMemo(() => {
    let filtered = [...results];
    if (filters.search.trim() !== '') {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(filters.search.toLowerCase())
      );
    }
    switch (filters.sort) {
      case 'price-asc':
        filtered.sort((a, b) => a.priceCOP - b.priceCOP);
        break;
      case 'price-desc':
        filtered.sort((a, b) => b.priceCOP - a.priceCOP);
        break;
      case 'name-asc':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      default:
        break;
    }
    return filtered;
  }, [results, filters]);

  const goToProduct = id => navigate(`/producto/${id}`);

  const handleAddToCart = (e, product) => {
    e.stopPropagation();
    dispatch(addToCart(product));
    showShopToast(`${product.name} agregado al carrito`, 'success');
  };

  const handleToggleWishlist = (e, product) => {
    e.stopPropagation();
    if (isInWishlist(product.id)) {
      dispatch(removeFromWishlist(product.id));
      setToastMessage(`${product.name} eliminado de la lista de deseos`);
    } else {
      dispatch(addToWishlist(product));
      setToastMessage(`${product.name} agregado a la lista de deseos`);
    }
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredResults.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(results.length / productsPerPage);

  const paginate = page => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <main>
      <ShopNav />
      <NavCategorias />
      <ServiceHighlights />

      <div className="container mt-5">
        <h3>Resultados para: <em>{query}</em></h3>
        <ProductFilter filters={filters} setFilters={setFilters} />

        {loading ? (
          <p>Cargando...</p>
        ) : results.length === 0 ? (
          <p>No se encontraron resultados.</p>
        ) : (
          <>
            <div className="row g-4">
              {currentProducts.map(product => {
                let pdfs = {};
                try {
                  if (product.pdfUrl) pdfs = JSON.parse(product.pdfUrl);
                } catch {}

                return (
                  <div key={product.id} className="col-md-6 col-lg-4">
                    <div
                      className="card h-100 shadow-sm border-0"
                      style={{ borderRadius: '1rem', cursor: 'pointer' }}
                      onClick={() => goToProduct(product.id)}
                    >
                      <div className="position-relative">
                        <ProductImage
                          imageUrl={product.imageUrl}
                          alt={product.name}
                          className="card-img-top"
                          style={{ height: '200px', objectFit: 'cover', borderTopLeftRadius: '1rem', borderTopRightRadius: '1rem' }}
                        />
                        <button
                          className="btn btn-light position-absolute top-0 end-0 m-2 rounded-circle shadow-sm"
                          onClick={e => handleToggleWishlist(e, product)}
                        >
                          <i className={`bi bi-heart${isInWishlist(product.id) ? '-fill text-danger' : ''}`}></i>
                        </button>
                      </div>

                      <div className="card-body d-flex flex-column">
                        <h5 className="card-title">{product.name}</h5>
                        <p className="card-text text-muted small flex-grow-1">{product.description}</p>
                        <div>
                          <p className="mb-1 fw-bold text-success">{formatCOP(product.priceCOP)}</p>
                          <p className="mb-2 text-muted small">USD: ${product.price}</p>
                          {pdfs.pdf1 && (
                            <a
                              href={pdfs.pdf1}
                              onClick={e => e.stopPropagation()}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-outline-primary w-100 mb-2"
                            >
                              Descargar PDF
                            </a>
                          )}
                          <button
                            className="btn btn-success btn-sm w-100"
                            onClick={e => handleAddToCart(e, product)}
                          >
                            Agregar al carrito
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Paginación */}
            <nav className="mt-4">
              <ul className="pagination justify-content-center">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => paginate(1)}>&laquo;</button>
                </li>
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => paginate(currentPage - 1)}>&lsaquo;</button>
                </li>
                {[...Array(totalPages).keys()].map(number => (
                  <li key={number + 1} className={`page-item ${currentPage === number + 1 ? 'active' : ''}`}>
                    <button onClick={() => paginate(number + 1)} className="page-link">
                      {number + 1}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => paginate(currentPage + 1)}>&rsaquo;</button>
                </li>
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => paginate(totalPages)}>&raquo;</button>
                </li>
              </ul>
            </nav>
          </>
        )}

        {/* Toast */}
        <div className="toast-container position-fixed bottom-0 end-0 p-3 shop-toast-container" style={{ zIndex: 9999 }}>
          <div className={`toast align-items-center text-bg-success border-0 ${showToast ? 'show' : 'hide'}`} role="alert" aria-live="assertive" aria-atomic="true">
            <div className="d-flex">
              <div className="toast-body">{toastMessage}</div>
              <button type="button" className="btn-close btn-close-white me-2 m-auto" onClick={() => setShowToast(false)}></button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </main>
  );
};

export default SearchResults;
